import { Suspense, useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { Center, Spinner, VStack, Text, Box } from '@chakra-ui/react';
import { useCourse } from '../contexts/CourseContext';
import { useCourseConfig } from '../hooks/useCourseConfig';
import { useConsentStatus } from '../hooks/useConsentStatus';
import { usePreferencesStatus } from '../hooks/usePreferencesStatus';

export default function CourseStepRenderer() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { isLoading: isLoadingCourse, currentCourse } = useCourse();
  const config = useCourseConfig();
  const { loading: loadingConsent, consentGiven } = useConsentStatus();
  const { loading: loadingPrefs, hasPreferences } = usePreferencesStatus();

  useEffect(() => {
    if (!isLoadingCourse && currentCourse && slug) {
      const exists = config.flow.some(s => s.slug === slug);
      if (!exists) {
        // Unknown slug for this course; redirect to index which will route correctly
        navigate('/', { replace: true });
      }
    }
  }, [slug, config.flow, isLoadingCourse, currentCourse, navigate]);

  if (isLoadingCourse || !slug) {
    return (
      <Center height="60vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  const step = config.flow.find(s => s.slug === slug);
  if (!step) {
    return (
      <Center height="60vh">
        <VStack spacing={3}>
          <Text>That step isn&apos;t available for your course.</Text>
          <Text as="button" onClick={() => navigate('/')} style={{ color: '#C200FB' }}>
            Go to course start
          </Text>
        </VStack>
      </Center>
    );
  }

  // Gate access to preferences, activity, and chat unless consent has been given
  if ((slug === 'preferences' || slug === 'activity' || slug === 'chat')) {
    if (loadingConsent) {
      return (
        <Center height="60vh">
          <Spinner size="xl" />
        </Center>
      );
    }
    if (!consentGiven) {
      return <Navigate to="/s/landing" replace />;
    }
    // Additional gating: chat requires saved preferences
    if (slug === 'chat') {
      if (loadingPrefs) {
        return (
          <Center height="60vh">
            <Spinner size="xl" />
          </Center>
        );
      }
      if (!hasPreferences) {
        return <Navigate to="/s/preferences" replace />;
      }
    }
  }

  const StepComponent = step.component as any;

  return (
    <Suspense fallback={
      <Center height="60vh">
        <Spinner size="xl" />
      </Center>
    }>
      <Box>
        <StepComponent />
      </Box>
    </Suspense>
  );
}
